/**
 * TaskTypeFilter — Chip-based filter for AgentTaskType
 *
 * Shows only task types that exist in the current task list.
 * Selected types narrow the displayed tasks; empty selection = show all.
 */

import React, { createElement } from 'react';
import { Box, Chip, SvgIconProps } from '@mui/material';
import { TASK_TYPE_CONFIG } from '../constants/taskTypeConfig';
import { TASKS_PANEL } from '../selectors/tasks-panel.sel';

interface TaskTypeFilterProps {
  /** Task types available in current list (derived by parent) */
  availableTypes: string[];
  /** Currently selected types (empty = all) */
  selectedTypes: string[];
  /** Callback when selection changes */
  onChange: (types: string[]) => void;
}

export const TaskTypeFilter: React.FC<TaskTypeFilterProps> = ({
  availableTypes,
  selectedTypes,
  onChange,
}) => {
  if (availableTypes.length === 0) return null;

  const handleToggle = (type: string) => {
    if (selectedTypes.includes(type)) {
      onChange(selectedTypes.filter((t) => t !== type));
    } else {
      onChange([...selectedTypes, type]);
    }
  };

  return (
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 2, py: 1 }}
      data-testid={TASKS_PANEL.taskTypeFilter}
    >
      {availableTypes.map((type) => {
        const config = TASK_TYPE_CONFIG[type];
        const isSelected = selectedTypes.includes(type);
        const label = config?.label ?? type;

        return (
          <Chip
            key={type}
            label={label}
            size="small"
            icon={
              config?.icon
                ? createElement(config.icon as React.ComponentType<SvgIconProps>, { sx: { fontSize: 14 } })
                : undefined
            }
            variant={isSelected ? 'filled' : 'outlined'}
            color={isSelected ? 'primary' : 'default'}
            onClick={() => handleToggle(type)}
            data-testid={`${TASKS_PANEL.taskTypeChipPrefix}${type}`}
            sx={{ height: 26, fontSize: '0.75rem' }}
          />
        );
      })}
    </Box>
  );
};
